/**
 * Types and calls for this surface, on the shared request wrapper.
 *
 * This file used to carry its own byte-identical copy of `request` from
 * lib/api.ts, because it was written while four agents held the tree and
 * lib/api.ts was another one's file. Two copies of the wrapper that turns a
 * non-2xx into a thrown Error is two places to fix when the error contract
 * changes, and the copy would have drifted the first time it did.
 */

import { request } from '../lib/api.js';

export interface RedlineSegment {
  text: string;
  kind: 'same' | 'del' | 'ins';
}

export type DraftChangeStatus = 'amended' | 'added' | 'removed' | 'moved';

export interface DraftChange {
  id: string;
  clauseNumber: string;
  heading: string | null;
  status: DraftChangeStatus;
  severity: 'substantive' | 'editorial';
  reasons: string[];
  redline: RedlineSegment[] | null;
}

export interface DraftSummary {
  id: string;
  label: string;
  importedAt: string;
  sourcePath: string;
  provisionCount: number;
  substantive: number;
  editorial: number;
}

export const draftApi = {
  list: () =>
    request<{ drafts: DraftSummary[]; available: boolean; unavailableBecause: string | null }>(
      'GET',
      '/drafts',
    ),

  detail: (id: string) =>
    request<{ draft: DraftSummary; changes: DraftChange[] }>('GET', `/drafts/${id}`),

  change: (id: string, number: string) =>
    request<DraftChange & { redline: RedlineSegment[] }>(
      'GET',
      `/drafts/${id}/changes/${encodeURIComponent(number)}`,
    ),
};
